import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
} from "@mui/material";
import classNames from "classnames";
import * as React from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { Button } from "@/shared/foundation/primitives";

import { CardConfig, CardKind } from "../DashboardHeader/DashboardParams";

import styles from "./AddCardPopover.module.css";

const KIND_LABEL: Record<CardKind, string> = {
  chart: "Chart",
  trend: "Trend",
};

export interface AddCardPopoverProps {
  /** Viewport coordinates to anchor at — the click that opened the flow. */
  position: { left: number; top: number };
  /** Which kinds have room at the target cell. A kind that doesn't fit is
   *  shown disabled rather than hidden, so the canvas's limits are visible. */
  fits: Record<CardKind, boolean>;
  /** The card being configured, or null while the type picker is showing.
   *  Nothing exists on the dashboard yet — this is a draft. */
  draft: CardConfig | null;
  onPickKind: (kind: CardKind) => void;
  /** Discard the draft and return to the type picker. */
  onBack: () => void;
  /** Commit the draft to the dashboard and close. The ONLY path that adds a
   *  card — Cancel, Escape and click-away all discard. */
  onCreate: () => void;
  /** Closed without creating anything (Cancel / Escape / click-away). */
  onClose: () => void;
  onDraftChange: (next: CardConfig) => void;
  /** Render the draft's data pickers. Supplied by the dashboard, which owns
   *  the metric definitions the pickers offer. */
  renderEditor: (
    card: CardConfig,
    onChange: (next: CardConfig) => void,
  ) => React.ReactNode;
}

/**
 * The add-card flow: pick a type, then configure the card, in one surface.
 *
 * Picking a type slides the popover across to the card's data editor rather
 * than dropping an unconfigured card on the grid — a back chevron returns to
 * the picker. Nothing reaches the dashboard until Create: Cancel, Escape and
 * click-away all leave the layout untouched.
 *
 * Anchored to pointer coordinates, like the type menu it replaces: the "+"
 * buttons it opens from are one per empty slot, so a single shared instance
 * positioned at the click beats a popover per slot. (That also rules out the
 * `shared/foundation` Popover, which anchors to a trigger element.)
 */
export function AddCardPopover({
  position,
  fits,
  draft,
  onPickKind,
  onBack,
  onCreate,
  onClose,
  onDraftChange,
  renderEditor,
}: AddCardPopoverProps) {
  const step = draft === null ? "kind" : "edit";
  const pickerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  const [height, setHeight] = useState<number>();

  // Hold the last draft so the editor keeps its content while it slides OUT
  // after Back — rendering `draft` directly would blank the pane mid-slide.
  // A render-time cache, not state: it never drives what this render shows.
  const lastDraft = useRef<CardConfig | null>(null);
  if (draft !== null) {
    lastDraft.current = draft;
  }
  const editorCard = draft ?? lastDraft.current;

  // The shell animates to the height of whichever pane is showing. Observed
  // rather than measured once: the editor's own height changes with the card
  // kind (only charts carry a Display picker).
  useLayoutEffect(() => {
    const active = step === "edit" ? editorRef.current : pickerRef.current;
    if (active === null) {
      return;
    }
    const measure = () => setHeight(active.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(active);
    return () => observer.disconnect();
  }, [step]);

  // The off-screen pane leaves the tab order and the accessibility tree.
  useLayoutEffect(() => {
    if (pickerRef.current) {
      pickerRef.current.inert = step !== "kind";
    }
    if (editorRef.current) {
      editorRef.current.inert = step !== "edit";
    }
  }, [step]);

  // Marking a pane inert blurs whatever was focused inside it, so the pane
  // arriving has to claim focus — otherwise focus falls to the body and
  // Escape no longer reaches the popover.
  //
  // Arriving at the editor that means the back button, not the Metric field:
  // an autofocused Autocomplete opens its listbox over the card being built.
  //
  // preventScroll because the pane taking focus is still off-view mid-slide,
  // and both it and the clipped shell can be scrolled programmatically — a
  // scroll-into-view here would drag the track sideways and leave the panes
  // misaligned with the window they slide through.
  useEffect(() => {
    if (step === "edit") {
      backRef.current?.focus({ preventScroll: true });
      return;
    }
    // Back lands on the kind that was being configured, so the trip out and
    // in returns you where you left rather than to the top of the list.
    const picker = pickerRef.current;
    const wasEditing = lastDraft.current;
    const target =
      (wasEditing === null
        ? null
        : picker?.querySelector<HTMLElement>(
            `[data-kind="${wasEditing.kind}"]:not([aria-disabled="true"])`,
          )) ??
      picker?.querySelector<HTMLElement>(
        '[role="menuitem"]:not([aria-disabled="true"])',
      );
    target?.focus({ preventScroll: true });
  }, [step]);

  return (
    <Popover
      open
      anchorReference="anchorPosition"
      anchorPosition={position}
      onClose={onClose}
      // The paper takes focus on open but has no intrinsic name, so without
      // this a screen reader announces nothing about what opened.
      slotProps={{ paper: { "aria-label": "Add a card" } }}
    >
      <div
        className={styles.shell}
        style={height === undefined ? undefined : { height }}
      >
        <div
          className={classNames(styles.track, {
            [styles.trackEditing]: step === "edit",
          })}
        >
          <div ref={pickerRef} className={styles.pane}>
            {/* Focus is driven by the step effect above, not autoFocusItem:
                that only lands when an item mounts, and these never remount
                across a round trip to the editor and back. */}
            <MenuList>
              <MenuItem
                data-kind="chart"
                disabled={!fits.chart}
                onClick={() => onPickKind("chart")}
              >
                <ListItemIcon>
                  <ShowChartIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={KIND_LABEL.chart} />
              </MenuItem>
              <MenuItem
                data-kind="trend"
                disabled={!fits.trend}
                onClick={() => onPickKind("trend")}
              >
                <ListItemIcon>
                  <TrendingUpIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={KIND_LABEL.trend} />
              </MenuItem>
            </MenuList>
          </div>
          <div ref={editorRef} className={styles.pane}>
            <header className={styles.header}>
              <IconButton
                ref={backRef}
                size="small"
                aria-label="Back to card type"
                onClick={onBack}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <span className={styles.title}>
                {editorCard === null ? "" : KIND_LABEL[editorCard.kind]}
              </span>
            </header>
            {editorCard !== null && renderEditor(editorCard, onDraftChange)}
            <div className={styles.footer}>
              <Button size="small" onClick={onClose}>
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={onCreate}>
                Create
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Popover>
  );
}
